/** Tip 138: resolve organizer vs guest + viewer RSVP from calendar event payloads. */

export type CalAttendeeLike = {
  email?: string;
  name?: string;
  displayName?: string;
  responseStatus?: string;
  self?: boolean;
  organizer?: boolean;
};

export type CalOrganizerLike = {
  email?: string;
  displayName?: string;
  self?: boolean;
};

export type CalEventRoleFields = {
  organizer?: string | CalOrganizerLike;
  organizerEmail?: string;
  creator?: string | CalOrganizerLike;
  isOrganizer?: boolean;
  self?: boolean;
  responseStatus?: string;
  accountEmail?: string;
  attendees?: Array<string | CalAttendeeLike>;
  guests?: Array<string | CalAttendeeLike>;
};

function normEmail(value: unknown): string {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function personEmail(value: unknown): string {
  if (typeof value === "string") return normEmail(value);
  if (value && typeof value === "object") {
    const o = value as CalOrganizerLike;
    return normEmail(o.email);
  }
  return "";
}

function attendeeList(event: CalEventRoleFields): Array<string | CalAttendeeLike> {
  const raw = event.attendees?.length ? event.attendees : event.guests;
  return Array.isArray(raw) ? raw : [];
}

/** True when the signed-in account owns / organizes the event (not calendar accessRole). */
export function isEventOrganizer(
  event: CalEventRoleFields | null | undefined,
  accountEmail?: string | null,
): boolean {
  if (!event) return false;
  if (event.isOrganizer === true) return true;
  if (event.isOrganizer === false) return false;

  const organizer = event.organizer;
  if (organizer && typeof organizer === "object" && organizer.self === true) return true;
  if (event.self === true && organizer) return true;

  const account = normEmail(accountEmail || event.accountEmail);
  if (!account) return false;

  const orgEmail =
    personEmail(organizer) ||
    normEmail(event.organizerEmail) ||
    personEmail(event.creator);
  if (orgEmail && orgEmail === account) return true;

  for (const entry of attendeeList(event)) {
    if (typeof entry === "string") continue;
    if (entry.organizer === true && (entry.self === true || normEmail(entry.email) === account)) {
      return true;
    }
  }
  return false;
}

/** Viewer's RSVP on this event (event-level or matching attendee.self / email). */
export function viewerResponseStatus(
  event: CalEventRoleFields | null | undefined,
  accountEmail?: string | null,
): string {
  if (!event) return "";
  const direct = String(event.responseStatus || "").trim();
  if (direct) return direct.toLowerCase();

  const account = normEmail(accountEmail || event.accountEmail);
  let selfMatch = "";
  let emailMatch = "";
  for (const entry of attendeeList(event)) {
    if (typeof entry === "string") continue;
    const status = String(entry.responseStatus || "").trim().toLowerCase();
    if (!status) continue;
    if (entry.self === true) selfMatch = status;
    if (account && normEmail(entry.email) === account) emailMatch = status;
  }
  return selfMatch || emailMatch || "";
}

/** Default "Also mark as Not attending" ON when viewer had accepted / tentative / maybe. */
export function defaultDeclineAlso(
  event: CalEventRoleFields | null | undefined,
  accountEmail?: string | null,
): boolean {
  const status = viewerResponseStatus(event, accountEmail);
  return (
    status === "accepted" ||
    status === "tentative" ||
    status === "tentativelyaccepted" ||
    status === "maybe"
  );
}
